import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { useSearchParams, useRouteLoaderData } from "react-router"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"

interface PaginationProps {
  totalCount?: number
  routeId: string
  pageSizes?: number[]
}

interface LoaderData {
  totalCount?: number
}

function Pagination({ totalCount, routeId, pageSizes = [10, 25, 50, 100] }: PaginationProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const loaderData = useRouteLoaderData<LoaderData>(routeId)
  const count = totalCount ?? loaderData?.totalCount ?? 0

  const pageParam = searchParams.get("page")
  const pageSizeParam = searchParams.get("pageSize")

  const page = pageParam !== null && pageParam !== "" ? +pageParam : 1
  const pageSize = pageSizeParam !== null && pageSizeParam !== "" ? +pageSizeParam : 10
  const totalPages = Math.ceil(count / pageSize)

  const from = Math.min(count, 1 + (page - 1) * pageSize)
  const to = Math.min(count, page * pageSize)

  if (count === 0) return null

  return (
    <div className="flex items-center justify-between gap-4 border-t p-4">
      <span className="text-sm text-gray-600">
        Showing {from} to {to} of {count} results
      </span>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm">Page size:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setSearchParams((params: URLSearchParams) => {
                params.set("page", "1")
                params.set("pageSize", value)
                return params
              })
            }}
          >
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => {
                setSearchParams((params: URLSearchParams) => {
                  params.set("page", String(page - 1))
                  return params
                })
              }}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => {
                setSearchParams((params: URLSearchParams) => {
                  params.set("page", String(page + 1))
                  return params
                })
              }}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Pagination
